import { Avatar } from "@/components/buttons/Avatar";
import { getTheme } from "@/features/branding";
import type { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { formatRelativeDate } from "@/utils/datetime";
import { withCommas } from "@/utils/number";
import { CheckCircleFilled, CheckCircleOutlined, CloseCircleOutlined, CloudDownloadOutlined, ExclamationCircleFilled, LoadingOutlined, RedoOutlined } from "@ant-design/icons";
import { Button, Col, Modal, Progress, Row } from "antd";
import { useEffect, useState } from "react";
import styles from "./SummaryProgressModal.module.scss";
interface SummaryProgressModalProps {
  open: boolean;
  schema: PortfolioExportJobSchema;
  isComplete: boolean;
  onClose: () => void;
  error: string;
  onRetry: () => Promise<void>;
  onRegenerate: () => Promise<void>;
  isRegenerating: boolean;
}

/**
 * Shows how much of the output has been completed.
 */
export const SummaryProgressModal = ({
  open,
  schema: _schema,
  isComplete,
  onClose,
  error,
  onRetry,
  onRegenerate,
  isRegenerating
}: SummaryProgressModalProps) => {
  // Store a local copy of the schema to prevent re-renders from background updates.
  const [schema, setSchema] = useState(_schema);
  const {
    antd
  } = getTheme();
  useEffect(() => {
    setSchema(_schema);
  }, [open]);
  const getDownloadingIcon = () => {
    if (isComplete) {
      if (error) return <CloseCircleOutlined />;
      return <CheckCircleOutlined />;
    }
    return <LoadingOutlined data-sentry-element="LoadingOutlined" data-sentry-component="getDownloadingIcon" data-sentry-source-file="SummaryProgressModal.tsx" />;
  };
  return <Modal open={open} width={640} closable={true} maskClosable={false} onCancel={onClose} title={"Building Summary Output"} footer={null} data-sentry-element="Modal" data-sentry-component="SummaryProgressModal" data-sentry-source-file="SummaryProgressModal.tsx">
      <Row gutter={[0, 0]} data-sentry-element="Row" data-sentry-source-file="SummaryProgressModal.tsx">
        <Col span={24} data-sentry-element="Col" data-sentry-source-file="SummaryProgressModal.tsx">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="grid items-center" style={{
            gridTemplateColumns: `72px 1fr`,
            gridAutoRows: "1.75em"
          }}>
              <div className="text-gray-500">Name</div>
              <div className="overflow-auto">{schema.name}</div>
              <div className="text-gray-500">{schema.isLargeSite ? "Points" : "Assets"}</div>
              <div>{withCommas(schema.assets.totalCount)}</div>
              <div className="text-gray-500">Group</div>
              <div>{schema.groupName}</div>
              <div className="text-gray-500">Created</div>
              <div className="flex items-center space-x-1">
                <Avatar name={schema.email} size={14} data-sentry-element="Avatar" data-sentry-source-file="SummaryProgressModal.tsx" /> <span>{schema.email}</span>
              </div>
              <div />
              <div>{schema.startTime > 0 ? `${formatRelativeDate(new Date(schema.startTime))}` : ""} </div>
            </div>
          </div>
        </Col>
        <div className="flex w-full flex-col divide-y text-base">
          <div className={`row-span-2 grid w-full py-4`}>
            <div className="flex justify-between">
              <div className="flex gap-2">
                {getDownloadingIcon()}
                <span className={isComplete ? "" : styles.loading}>
                  {schema.summaryId && schema.isSummaryGenerated ? "Retrieving saved summary" : "Building your summary"}
                </span>
              </div>
            </div>
            <Progress percent={error ? 0 : 100} showInfo={false} strokeColor={isComplete ? antd.token?.colorPrimary : "#ddd"} className={isComplete ? styles.progressBar : styles.progressBarIndefiniteLoading} data-sentry-element="Progress" data-sentry-source-file="SummaryProgressModal.tsx" />
          </div>
          {isComplete && error && <>
              <div className="flex flex-col justify-between gap-y-2 py-4">
                <div className="flex w-full gap-x-2 text-red-600">
                  <ExclamationCircleFilled />
                  <span className="font-medium">Error: {error}</span>
                </div>
                <div className="flex gap-x-2 pl-6">
                  <Button type="primary" onClick={onRetry} icon={<RedoOutlined />}>
                    Retry
                  </Button>
                  <Button icon={<CloudDownloadOutlined />} loading={isRegenerating} onClick={async () => {
                setSchema({
                  ..._schema,
                  isSummaryGenerated: false
                });
                await onRegenerate();
              }}>
                    Regenerate Summary
                  </Button>
                </div>
              </div>
            </>}
          {isComplete && error === "" && <div className="flex justify-between py-4">
              <div className="flex gap-x-2 text-green-600">
                <CheckCircleFilled />
                <span className="font-medium">Download complete!</span>
              </div>
            </div>}
        </div>
      </Row>
    </Modal>;
};